import { useEffect, useState } from "react";
import { usePlant } from "@/lib/context";
import api from "@/lib/api";
import { num, intf, inrFull, COLORS, statusColor } from "@/lib/format";
import { ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from "recharts";
import { AlertTriangle, Droplets } from "lucide-react";

export default function Charge() {
  const { state } = usePlant();
  const [heats, setHeats] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/plant/heats?limit=40");
        if (alive) setHeats(r.data.heats || []);
      } catch {}
    };
    load();
    const id = setInterval(load, 10000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!state) return <div className="p-10 text-[#8F93A2]">Loading…</div>;
  const m = state.metrics || {};
  const last = state.shift?.last_heat_summary || {};
  const cqs = m.ce_26 || 75;
  const cqsColor = cqs >= 75 ? COLORS.green : cqs >= 60 ? COLORS.yellow : COLORS.red;

  const totalKg = last.total_kg || 500;
  const mix = [
    { name: "Returns", value: last.returns_kg || 250, fill: COLORS.green },
    { name: "Pig Iron", value: last.pig_iron_kg || 175, fill: COLORS.blue },
    { name: "Clean Scrap", value: last.scrap_clean_kg || 75, fill: COLORS.yellow },
    { name: "Contaminated", value: last.scrap_contam_kg || 0, fill: COLORS.red },
  ];

  const cqsTrend = heats.slice(0, 24).reverse().map((h, i) => ({
    i: h.heat_number,
    cqs: h.cqs,
  }));

  const isWet = state.furnaces?.some((f) => state.shift?.last_heat_summary?.wet_flag) || last.wet_flag;
  const ce27 = m.ce_27 || 800;

  return (
    <div className="space-y-5">
      <div>
        <div className="label-small">Charge Intelligence</div>
        <h1 className="font-display text-3xl uppercase tracking-tight font-bold">Material Quality → Energy Cost</h1>
      </div>

      {(isWet || last.wet_flag) && (
        <div className="panel border-2 border-[#FF3333] p-4 flex items-center gap-3" data-testid="wet-charge-warning">
          <AlertTriangle size={28} className="text-[#FF3333]" />
          <div className="flex-1">
            <div className="font-display uppercase tracking-wider text-lg font-bold text-[#FF3333]">Wet Charge Detected</div>
            <div className="text-sm text-white/90">
              Estimated SEC penalty: <span className="font-mono">~105 kWh/tonne</span> · Cost per heat: <span className="font-mono text-[#FF3333]">{inrFull(105 * 0.5 * state.plant_params?.tariff_per_kwh)}</span>
              · Safety risk: explosion hazard with wet material in molten metal.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-4 panel p-5" data-testid="cqs-card">
          <div className="label-small">Charge Quality Score — Current Heat</div>
          <div className="metric-massive mt-2" style={{ color: cqsColor }} data-testid="cqs-value">{num(cqs, 0)}</div>
          <div className="text-xs text-[#8F93A2] font-mono">of 100</div>
          <div className="mt-4 space-y-2">
            <CqsBar label="Returns ratio score" value={last.cqs_components?.returns_score ?? 72} target={100} />
            <CqsBar label="Contamination score" value={last.cqs_components?.contam_score ?? 95} target={100} />
            <CqsBar label="Moisture score" value={last.cqs_components?.moisture_score ?? 98} target={100} />
            <CqsBar label="Scrap ratio score" value={last.cqs_components?.scrap_ratio_score ?? 80} target={100} />
          </div>
        </div>

        <div className="col-span-12 md:col-span-4 panel">
          <div className="panel-header"><span>Material Mix</span></div>
          <div className="p-3 h-[280px]" data-testid="material-mix-chart">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={mix} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} stroke="none" label={(e) => `${e.name}`}>
                  {mix.map((m, i) => <Cell key={i} fill={m.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 md:col-span-4 panel p-5" data-testid="charge-cost-card">
          <div className="label-small">Energy Cost — Current Charge Mix</div>
          <div className="metric-mid mt-2 text-[#00E5FF]">{num(ce27, 0)}<span className="text-xs ml-1">kWh/t</span></div>
          {(() => {
            const bestPossible = m.ce_27_best || ce27;
            const gap = Math.max(0, ce27 - bestPossible);
            const heatDiffKwh = gap * (totalKg / 1000.0);
            const heatDiffRupees = heatDiffKwh * (state.plant_params?.tariff_per_kwh || 6.5);
            if (gap < 5) {
              return (
                <>
                  <div className="text-xs text-[#00FF66] mt-1 font-display uppercase tracking-wider">Optimal mix for available materials</div>
                  <div className="mt-4 p-3 bg-[#14151C] border-l-2 border-[#00FF66] text-xs leading-relaxed">
                    This charge mix is the most energy-efficient possible with the materials currently on hand.
                    Further improvement requires sourcing higher-quality returns or cleaner scrap from the stores.
                  </div>
                </>
              );
            }
            return (
              <>
                <div className="text-xs text-[#8F93A2] mt-1">Best possible with available materials: <span className="text-white">{num(bestPossible, 0)} kWh/t</span></div>
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-[#8F93A2]">Difference per heat</div>
                  <div className="metric-mid text-[#FF3333]">{inrFull(heatDiffRupees)}</div>
                </div>
                <div className="mt-4 text-xs leading-relaxed text-white/90">
                  This charge mix consumes <span className="font-mono">{num(ce27, 0)}</span> kWh/tonne vs. an
                  achievable <span className="font-mono text-[#00FF66]">{num(bestPossible, 0)}</span>. Cleaning
                  contamination and drying any wet material could save <span className="font-mono text-[#00FF66]">{inrFull(heatDiffRupees * 17 * 26)}/month</span>.
                </div>
              </>
            );
          })()}
        </div>

        <div className="col-span-12 panel">
          <div className="panel-header"><span>CQS Trend — last 24 heats</span></div>
          <div className="p-3 h-[200px]" data-testid="cqs-trend-chart">
            <ResponsiveContainer>
              <LineChart data={cqsTrend}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="i" stroke={COLORS.textDim} fontSize={10} />
                <YAxis stroke={COLORS.textDim} fontSize={10} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <ReferenceLine y={75} stroke={COLORS.green} strokeDasharray="3 3" />
                <ReferenceLine y={60} stroke={COLORS.red} strokeDasharray="3 3" />
                <Line type="monotone" dataKey="cqs" stroke={COLORS.blue} strokeWidth={2} dot  isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 panel">
          <div className="panel-header"><span>Incoming Material Log</span></div>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm" data-testid="material-log-table">
              <thead className="text-[#8F93A2] uppercase tracking-wider text-[10px]">
                <tr className="border-b border-[#1F212A]">
                  <th className="text-left p-2">Heat</th>
                  <th className="text-left p-2">Time</th>
                  <th className="text-right p-2">Returns</th>
                  <th className="text-right p-2">Pig</th>
                  <th className="text-right p-2">Scrap clean</th>
                  <th className="text-right p-2">Contam</th>
                  <th className="text-right p-2">Wet</th>
                  <th className="text-right p-2">CQS</th>
                  <th className="text-right p-2">SEC achieved</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                {heats.slice(0, 12).map((h, idx) => (
                  <tr key={h.id || `mat-${h.furnace_id}-${h.heat_number}-${idx}`} className="border-b border-[#14151C]">
                    <td className="p-2">{h.heat_number}</td>
                    <td className="p-2 text-[#8F93A2]">{new Date(h.end_plant_time).toLocaleTimeString("en-IN", { hour12: false })}</td>
                    <td className="p-2 text-right">{intf(h.charge_returns_kg)}</td>
                    <td className="p-2 text-right">{intf(h.charge_pig_iron_kg)}</td>
                    <td className="p-2 text-right">{intf(h.charge_scrap_clean_kg)}</td>
                    <td className={`p-2 text-right ${h.charge_scrap_contam_kg > 20 ? "text-[#FF3333]" : ""}`}>{intf(h.charge_scrap_contam_kg)}</td>
                    <td className="p-2 text-right">{h.charge_wet_flag ? <span className="text-[#FF3333]">YES</span> : "—"}</td>
                    <td className={`p-2 text-right ${h.cqs < 60 ? "text-[#FF3333]" : h.cqs < 75 ? "text-[#FFCC00]" : "text-[#00FF66]"}`}>{num(h.cqs, 0)}</td>
                    <td className="p-2 text-right">{num(h.sec_kwh_per_t, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function CqsBar({ label, value, target, reverse }) {
  const v = Math.max(0, Math.min(100, value));
  const color = v > 75 ? COLORS.green : v > 50 ? COLORS.yellow : COLORS.red;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-[#8F93A2] uppercase tracking-wider">{label}</span>
        <span className="font-mono">{num(v, 0)}</span>
      </div>
      <div className="h-1.5 bg-[#14151C]">
        <div className="h-full" style={{ width: `${v}%`, background: color }} />
      </div>
    </div>
  );
}
